import React from 'react'
import { useLocation } from 'react-router-dom'
import { WifiOff } from 'lucide-react'
import { useBackendHealth } from '@/hooks/useBackendHealth'
import { useAuthStore } from '@/stores/authStore'

/**
 * Top-of-app banner that surfaces a backend-unreachable state.
 *
 * Renders a single instance near the app root and is intentionally
 * non-dismissible — the user must see that writes will fail until the server
 * is back. Hidden on public auth routes (login, password reset) since the
 * auth flow itself is the canary.
 */
export function OfflineBanner(): React.ReactElement | null {
  const { status } = useBackendHealth()
  const location = useLocation()
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)

  // Suppress on public auth routes — login itself surfaces the failure.
  const isAuthRoute =
    location.pathname.startsWith('/login') ||
    location.pathname.startsWith('/forgot-password') ||
    location.pathname.startsWith('/reset-password')

  if (isAuthRoute || !isAuthenticated) return null
  if (status !== 'offline') return null

  // MIDNIGHT warm-red tinted glass (re-using blush pastel #F4B5BD).
  const tint = '#F4B5BD'
  const tileBg = 'rgba(244,181,189,0.12)'
  const tileBorder = 'rgba(244,181,189,0.32)'

  return (
    <div
      role="alert"
      aria-live="assertive"
      data-testid="offline-banner"
      className="animate-fade-up"
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 50,
        background: tileBg,
        backdropFilter: 'blur(24px) saturate(160%)',
        WebkitBackdropFilter: 'blur(24px) saturate(160%)',
        borderBottom: `0.5px solid ${tileBorder}`,
        padding: '10px 16px',
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        color: tint,
        fontSize: 13,
        lineHeight: 1.4,
        letterSpacing: '-0.01em',
      }}
    >
      <span
        aria-hidden="true"
        style={{
          width: 28,
          height: 28,
          borderRadius: 9999,
          background: 'rgba(244,181,189,0.18)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <WifiOff style={{ width: 14, height: 14, color: tint }} />
      </span>
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ fontWeight: 600 }}>Connection lost.</span>{' '}
        We're trying to reach the server — any edits you make may not save.
        We'll let you know as soon as we're back online.
      </span>
    </div>
  )
}

export default OfflineBanner
